import { useEffect } from 'react';
import { useLanguage } from './LocalizationContext';

const TRANSLATABLE_ATTRIBUTES = [
  'placeholder',
  'title',
  'aria-label',
  'aria-description',
  'alt',
  'value',
] as const;
const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE']);
const TRANSLATABLE_INPUT_TYPES = new Set(['button', 'reset', 'submit']);
const originalTextNodes = new WeakMap<Text, string>();
const lastTranslatedTextNodes = new WeakMap<Text, string>();

const hasLatinText = (value: string) => /[A-Za-z]/.test(value);

const splitOuterWhitespace = (value: string) => {
  const match = value.match(/^(\s*)([\s\S]*?)(\s*)$/);
  return {
    leading: match?.[1] ?? '',
    body: match?.[2] ?? value,
    trailing: match?.[3] ?? '',
  };
};

const shouldSkipTextNode = (node: Text) => {
  const parent = node.parentElement;
  if (!parent) return true;
  if (IGNORED_TAGS.has(parent.tagName)) return true;
  if (parent.closest('[data-i18n-skip="true"], [translate="no"], .notranslate')) return true;
  return false;
};

const shouldTranslateAttribute = (element: Element, attributeName: string) => {
  if (attributeName !== 'value') return true;
  if (!(element instanceof HTMLInputElement)) return false;
  return TRANSLATABLE_INPUT_TYPES.has(element.type.toLowerCase());
};

const translateTextNode = (
  node: Text,
  language: string,
  translate: (key: string) => string
) => {
  if (shouldSkipTextNode(node)) return;

  const currentValue = node.nodeValue ?? '';
  const { leading, body, trailing } = splitOuterWhitespace(currentValue);
  const trimmedBody = body.trim();
  if (!trimmedBody) return;

  let originalValue = originalTextNodes.get(node);
  const lastTranslatedValue = lastTranslatedTextNodes.get(node);

  if (language === 'en') {
    if (originalValue && currentValue === lastTranslatedValue && currentValue !== originalValue) {
      node.nodeValue = originalValue;
      lastTranslatedTextNodes.set(node, originalValue);
    } else if (originalValue && currentValue !== originalValue && currentValue !== lastTranslatedValue) {
      originalTextNodes.set(node, currentValue);
      lastTranslatedTextNodes.set(node, currentValue);
    }
    return;
  }

  if (originalValue && currentValue !== originalValue && currentValue !== lastTranslatedValue) {
    originalValue = currentValue;
    originalTextNodes.set(node, currentValue);
  }

  const source = originalValue ?? currentValue;
  if (!hasLatinText(source)) return;
  const sourceParts = splitOuterWhitespace(source);
  const translatedBody = translate(sourceParts.body.trim());
  if (translatedBody === sourceParts.body.trim()) return;

  if (!originalValue) {
    originalTextNodes.set(node, source);
  }

  const nextValue = `${leading}${translatedBody}${trailing}`;
  lastTranslatedTextNodes.set(node, nextValue);
  if (node.nodeValue !== nextValue) {
    node.nodeValue = nextValue;
  }
};

const originalAttributeName = (attributeName: string) =>
  `data-i18n-original-${attributeName.replace(':', '-')}`;

const lastTranslatedAttributeName = (attributeName: string) =>
  `data-i18n-last-${attributeName.replace(':', '-')}`;

const translateElementAttributes = (
  element: Element,
  language: string,
  translate: (key: string) => string
) => {
  if (IGNORED_TAGS.has(element.tagName)) return;
  if (element.closest('[data-i18n-skip="true"], [translate="no"], .notranslate')) return;

  TRANSLATABLE_ATTRIBUTES.forEach((attributeName) => {
    if (!shouldTranslateAttribute(element, attributeName)) return;

    const storageName = originalAttributeName(attributeName);
    const lastStorageName = lastTranslatedAttributeName(attributeName);
    const currentValue = element.getAttribute(attributeName);
    let originalValue = element.getAttribute(storageName);
    const lastTranslatedValue = element.getAttribute(lastStorageName);

    if (language === 'en') {
      if (originalValue && currentValue === lastTranslatedValue && currentValue !== originalValue) {
        element.setAttribute(attributeName, originalValue);
        element.setAttribute(lastStorageName, originalValue);
      } else if (
        originalValue &&
        currentValue &&
        currentValue !== originalValue &&
        currentValue !== lastTranslatedValue
      ) {
        element.setAttribute(storageName, currentValue);
        element.setAttribute(lastStorageName, currentValue);
      }
      return;
    }

    if (!currentValue) return;
    if (originalValue && currentValue !== originalValue && currentValue !== lastTranslatedValue) {
      originalValue = currentValue;
      element.setAttribute(storageName, currentValue);
    }

    const source = originalValue ?? currentValue;
    if (!hasLatinText(source)) return;
    const translated = translate(source);
    if (translated === source) return;

    if (!originalValue) {
      element.setAttribute(storageName, source);
    }
    element.setAttribute(lastStorageName, translated);
    if (currentValue !== translated) {
      element.setAttribute(attributeName, translated);
    }
  });
};

const walkAndTranslate = (
  root: Node,
  language: string,
  translate: (key: string) => string
) => {
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root as Text, language, translate);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

  if (root.nodeType === Node.ELEMENT_NODE) {
    translateElementAttributes(root as Element, language, translate);
  }

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT
  );

  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      translateTextNode(node as Text, language, translate);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      translateElementAttributes(node as Element, language, translate);
    }
    node = walker.nextNode();
  }
};

const TranslationDomBridge: React.FC = () => {
  const { language, t } = useLanguage();

  useEffect(() => {
    if (typeof document === 'undefined' || !document.documentElement) return;

    let scheduled = false;
    const translateDocument = () => {
      scheduled = false;
      walkAndTranslate(document.documentElement, language, t);
    };

    const scheduleTranslate = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(translateDocument);
    };

    scheduleTranslate();

    const observer = new MutationObserver((mutations) => {
      let shouldTranslate = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          shouldTranslate = true;
          return;
        }

        if (mutation.type === 'attributes') {
          shouldTranslate = true;
          return;
        }

        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldTranslate = true;
        }
      });

      if (shouldTranslate) {
        scheduleTranslate();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...TRANSLATABLE_ATTRIBUTES],
    });

    const nativeAlert = window.alert.bind(window);
    const nativeConfirm = window.confirm.bind(window);
    const nativePrompt = window.prompt.bind(window);
    const localizeMessage = (message?: string) => {
      const value = String(message ?? '');
      return language === 'en' ? value : t(value);
    };
    const localizedAlert: typeof window.alert = (message) =>
      nativeAlert(localizeMessage(String(message ?? '')));
    const localizedConfirm: typeof window.confirm = (message) =>
      nativeConfirm(localizeMessage(String(message ?? '')));
    const localizedPrompt: typeof window.prompt = (message, defaultValue) =>
      nativePrompt(localizeMessage(message), defaultValue);

    window.alert = localizedAlert;
    window.confirm = localizedConfirm;
    window.prompt = localizedPrompt;

    return () => {
      observer.disconnect();
      if (window.alert === localizedAlert) window.alert = nativeAlert;
      if (window.confirm === localizedConfirm) window.confirm = nativeConfirm;
      if (window.prompt === localizedPrompt) window.prompt = nativePrompt;
    };
  }, [language, t]);

  return null;
};

export default TranslationDomBridge;
